import { useEffect, useState, memo } from "react";

interface ConfettiPiece {
  id: number;
  x: number;
  delay: number;
  duration: number;
  color: string;
  size: number;
  rotation: number;
}

interface ConfettiProps {
  active: boolean;
  duration?: number;
  pieceCount?: number;
}

const COLORS = [
  "var(--amber-400)",
  "var(--amber-500)",
  "var(--amber-600)",
  "var(--emerald-400)",
  "var(--sky-400)",
  "var(--burgundy-400)",
  "var(--stone-300)",
];

function generatePieces(count: number): ConfettiPiece[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2 + Math.random() * 2,
    color: COLORS[Math.floor(Math.random() * COLORS.length)] ?? "var(--amber-400)",
    size: 8 + Math.random() * 8,
    rotation: Math.random() * 360,
  }));
}

export const Confetti = memo(function Confetti({
  active,
  duration = 4000,
  pieceCount = 50,
}: ConfettiProps) {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (active) {
      setPieces(generatePieces(pieceCount));
      setVisible(true);

      const timeout = setTimeout(() => {
        setVisible(false);
      }, duration);

      return () => clearTimeout(timeout);
    } else {
      setVisible(false);
      return undefined;
    }
  }, [active, duration, pieceCount]);

  if (!visible) return null;

  return (
    <div className="confetti" aria-hidden="true">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="confetti__piece"
          style={{
            left: `${piece.x}%`,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
            backgroundColor: piece.color,
            width: `${piece.size}px`,
            height: `${piece.size}px`,
            transform: `rotate(${piece.rotation}deg)`,
          }}
        />
      ))}
    </div>
  );
});
